import React, { JSX } from 'react';
import { cn, Input } from '@ds';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { MdOutlineMap } from 'react-icons/md';
import { CSSchedulerSettingsTimeGroupInputsFormType } from '@lp/features/Scheduler/SchedulerClinicalSpecialistsSettings/types';
import { getNumberInputValue, getNumberInputValueHandler } from '@lp/features/Scheduler/utils';
import { Hub, Region, CSShiftTimeGroup } from '@/api/apis/resources/schedule';
import { OrganType, OrganTypeWithoutKidney } from '@/api/apis/case';
import { getColorsByOrganType, getKeys } from '@/utils/helpers';
import { shiftTimeGroupNames } from '@/constants/schedule.constants';

type ClinicalSpecialistsOnCallFormPropTypes = {
  isReadOnly: boolean;
  regions: Region[];
};

// TMDXLP-14091: Kidneys hidden from CS Settings On Call table below (data itself is untouched,
// only the rendered columns are filtered). Revert OrganTypeWithoutKidney -> OrganType to restore.
const organList = Object.values(OrganTypeWithoutKidney) as OrganType[];
// const organList = Object.values(OrganType);

export const CSTimeGroupOnCallFormSection = ({ regions, isReadOnly }: ClinicalSpecialistsOnCallFormPropTypes) => {
  const { control } = useFormContext<CSSchedulerSettingsTimeGroupInputsFormType>();
  const [perHubs, perRegions] = useWatch({ control, name: ['perHubs', 'perRegions'] });

  const renderTableCells = (region?: Region, hub?: Hub) => {
    const source = hub ? perHubs[hub.id] : perRegions[region!.id];

    let tableCells: JSX.Element[] = source
      ? [
          <td className="rounded-s-lg px-3" key={`${hub ? hub.id : region?.id}`}>
            <span
              className={`text-sm ${region ? 'font-bold flex items-center' : ''} ${hub ? 'ml-3 text-ellipsis block overflow-hidden' : ''}`}
            >
              {region && <MdOutlineMap size={24} className="mr-2" />}
              {hub ? hub.name : region?.name}
            </span>
          </td>,
        ]
      : [];

    if (source) {
      organList.forEach((organ) => {
        const organColors = getColorsByOrganType(organ);

        getKeys(source[organ]).forEach((timeGroup) => {
          getKeys(source[organ][timeGroup]).forEach((key) => {
            const fieldName =
              `${hub ? 'perHubs' : 'perRegions'}.${hub ? hub.id : region!.id}.${organ}.${timeGroup}.${key}` as const;
            const bgColor = hub ? organColors.bg90 : organColors.bg70;

            tableCells = [
              ...tableCells,
              <td
                className={cn('h-12 max-w-20 last:rounded-e-lg px-2', bgColor)}
                key={`${hub ? hub.id : region?.id}${organ}${timeGroup}${key}`}
              >
                <Controller
                  name={fieldName}
                  control={control}
                  render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
                    <Input
                      type="number"
                      min={0}
                      aria-label={fieldName}
                      isInvalid={invalid}
                      isDisabled={isReadOnly}
                      errorMessage={error?.message}
                      value={getNumberInputValue(value)}
                      onValueChange={getNumberInputValueHandler(onChange)}
                      {...field}
                    />
                  )}
                />
              </td>,
            ];
          });
        });
      });
    }

    return tableCells;
  };

  const renderTableRows = () => {
    let tableRows: JSX.Element[] = [
      <tr className="h-7 text-base-25 text-s-r text-end [&>td]:px-3" key="row0">
        <td className="text-start">Required min value</td>
        {organList.flatMap((organ) => {
          const colors = getColorsByOrganType(organ);

          return Object.values(CSShiftTimeGroup).map((timeGroup) => (
            <React.Fragment key={`${organ}-${timeGroup}`}>
              <td className={colors.bg90}>Strict {shiftTimeGroupNames[timeGroup]}</td>
              <td className={cn(colors.bg90, 'last-of-type:rounded-e-lg')}>Flex {shiftTimeGroupNames[timeGroup]}</td>
            </React.Fragment>
          ));
        })}
      </tr>,
    ];

    regions.forEach((region: Region) => {
      tableRows.push(
        <tr key={`row${region.id}`} className="bg-default-100">
          {renderTableCells(region, undefined)}
        </tr>,
      );

      region.hubs.forEach((hub) => {
        tableRows = [...tableRows, <tr key={`hubRow${hub.id}`}>{renderTableCells(undefined, hub)}</tr>];
      });
    });

    return tableRows;
  };

  return (
    <div className="flex-1 max-w-350">
      <h2 className="text-hm text-[#111] mb-3">Minimum Clinical Specialists On Call Everyday</h2>
      <table
        aria-label="Schedule table"
        className="relative border-separate border-spacing-0 px-4 py-4 bg-white rounded-lg w-full"
      >
        <thead className="bg-default-100">
          <tr>
            <th className="rounded-s-lg" aria-label="Placeholder" />
            {organList.map((organ) => (
              <th
                key={organ}
                aria-colspan={4}
                colSpan={4}
                className="text-right text-foreground-500 text-tiny font-semibold px-3 py-3 uppercase last-of-type:rounded-e-lg"
              >
                {organ}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{renderTableRows()}</tbody>
      </table>
    </div>
  );
};
